import React from "react";
import CustomLink from "../link/customLink";
import _ from "lodash";
import classNames from "classnames";
import PropTypes from "prop-types";
import FormGroupPlainText from "../../formGroupPlainText";
import FormGroupCheckBox from "../formGroupCheckbox";

const TableAccordion = (props) => {
    const {
        data,
        expandedAccordionItems,
        columns,
        onExpandAccordionItems,
        selectable,
        selected,
        onDatumSelect,
        accordionItemHeaderFormatter,
        hasActions,
        actions,
        renderAsList,
        onTableRowExpand,
        expandedTableRows,
        expandableRow,
        customNestedTableComponent,
        loadingRows,
    } = props;

    const ActionsCell = ({actions, datum, rowIndex}) => {
        return (
            <div>
                {
                    actions.map((action, index) => {
                        if(action.customComponent){
                            const Comp = action.customComponent;
                            return <Comp key={index} datum={datum} action={action} rowIndex={rowIndex}/>
                        }
                        return (
                            <div key={index} className={"inlineBlock"}>
                                <CustomLink
                                    label={action.label}
                                    onClick={() => action.callback(datum, rowIndex)}
                                    to={action.handleRedirect ? action.handleRedirect(datum) : null}
                                />
                            </div>
                        )
                    })
                }
            </div>
        )
    };

    return (
        <div className={classNames("arag", "panel-group")} role="tablist" aria-multiselectable={true}>
            {
                data.map((datum, index) => (
                    <div className={classNames(
                        "panel",
                        "panel-default"
                    )} key={index}>
                        <div className={"panel-heading"} role="tab" onClick={() => onExpandAccordionItems(index)}>
                            <h4 className={"panel-title"}>
                                <span
                                    role="button"
                                    data-toggle="collapse"
                                    data-parent="#accordion"
                                    aria-expanded="false"
                                    aria-controls="collapseOne"
                                    className="collapsed">
                                    {
                                        accordionItemHeaderFormatter ?
                                            accordionItemHeaderFormatter(datum) : `Item #${index + 1}`
                                    }
                                </span>
                            </h4>
                        </div>
                        {expandedAccordionItems.includes(index) && (
                            <div className={
                                classNames(
                                    "panel-collapse",
                                    "collapse",
                                    "in"
                                )} role="tabpanel" id="collapseOne" aria-labelledby="headingOne" aria-expanded="false">
                                <div className={"panel-body"}>
                                    {renderAsList && (
                                        <div>{datum.element}</div>
                                    )}
                                    {!renderAsList && (
                                        [
                                            <form className="form-horizontal" key={0}>
                                                <fieldset>
                                                    {
                                                        columns.map(column => (
                                                            <FormGroupPlainText
                                                                key={column.header}
                                                                leftWrapper="col-sm-3"
                                                                Label={column.header}
                                                                InputWrapper="col-sm-7"
                                                                Text={column.formatter ?
                                                                    column.formatter(datum): _.get(datum, column.propertyName)}
                                                            />
                                                        ))
                                                    }
                                                    {selectable && (
                                                        <FormGroupCheckBox
                                                            leftClassName={'col-sm-3'}
                                                            centerClassName={'col-sm-7'}
                                                            checked={!!(selectable && selected[index])}
                                                            name={`checkbox_${index}`}
                                                            handleChange={(evt) => onDatumSelect(evt.target.checked, index, datum)}
                                                        />
                                                    )}
                                                    {hasActions && (
                                                        <ActionsCell actions={actions} datum={datum} rowIndex={index}/>
                                                    )}
                                                    {(expandableRow && expandedTableRows) && (
                                                        <div className={"form-group"}>
                                                            <CustomLink
                                                                label={expandedAccordionItems.includes(index) ? "close" : "open"}
                                                                onClick={() => onTableRowExpand(index)}
                                                            />
                                                        </div>
                                                    )}
                                                </fieldset>
                                            </form>
                                        ]
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))
            }
        </div>
    );
};

TableAccordion.propTypes = {
    /** Array that contains the rows indexes that should have a loader */
    loadingRows: PropTypes.arrayOf(PropTypes.number),
    /** The data that should be displayed. Can by an array of objects of any type and shape */
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    /** The columns of the list - table. The propertyName is used to match the desired property of the data objects.
     * eg: the data object {email : "user@test.io"} will be matched by the column: {propertyName: "email", header:
     * "Email!"}. If you wish to access nested properties from the data you should specify them again in the
     * propertyName separated with a dot "." eg: data: {user: {email : "user@test.io"}}, then the property name will be
     * propertyName: "user.email". The id property of is used for sorting. So it must match the correct sortBy key.
     * The formatter is a callback used to format the data as you wish. It is invoked with the datum. For example
     * formatting timestamp dates. the sortable prop specifies if the column is sortable. Property is required if not
     * rendered as a list*/
    columns: PropTypes.arrayOf(PropTypes.shape({
        propertyName: PropTypes.string.isRequired,
        header: PropTypes.string.isRequired,
        id: PropTypes.string,
        formatter: PropTypes.func,
        sortable: PropTypes.bool
    })),
    /** Array that contains the indexes of the expanded rows - panel items*/
    expanded: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    /** Callback invoked with the entire row - datum in order to format the accordion item header as you wish!*/
    accordionItemHeaderFormatter: PropTypes.func,
    /** Boolean indicating that the items are selectable. If set to true, the props selected, onDatumSelect
     *  MUST be specified */
    selectable: PropTypes.bool,
    /** Renders the component as an accordion list. If this is the case then the data array must contain the list items */
    renderAsList: PropTypes.bool,
    /** Object containing the selected items */
    selected: PropTypes.object,
    /** Callback invoked  when the use checks a row - item. It will be passed with the arguments: checked(bool),
     * index(number), datum(object), eg: (checked, index, datum) => {}*/
    onDatumSelect: PropTypes.func,
    /** Boolean prop that when is set to true an extra column will be displayed to display the actions - links.
     * If set to true the property actions must be specified */
    hasActions: PropTypes.bool,
    /** Array of the desired actions. The callback will be invoked when the corresponding action is clicked.
     * The callback function with have the row datum and row index passed as arguments. */
    actions: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        callback: PropTypes.func.isRequired
    })),
    /** object that describes the expandable row table. You can also add all the properties of The Table component
     * in the property nestedTableProps and they will be passed to the nested component. If you want to map-alter the
     * data before display use the dataFormatter function property. It will be called with the data for display. It
     * MUST return the altered data.*/
    expandableRow: PropTypes.shape({
        columns:  PropTypes.arrayOf(PropTypes.shape({
            propertyName: PropTypes.string.isRequired,
            header: PropTypes.string.isRequired,

            formatter: PropTypes.func,
        })),
        propertyNameWithData: PropTypes.string.isRequired,
        dataFormatter: PropTypes.func,
        nestedTableProps: PropTypes.object
    }),
    /** Custom component to replace the built in nested Table component of each row. It will have the
     * expandable table row passed as a prop */
    customNestedTableComponent: PropTypes.element,
};

export default TableAccordion;